import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { MonthPicker } from '../components/ui/date-picker';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { BookOpen, Filter, CheckCircle2, Circle, Eye, Tag, User, ChevronDown, ChevronUp, Calendar, Lock, Sparkles, Bookmark, X, ZoomIn, RotateCcw, Flag, Check, ChevronsUpDown, History, FolderTree } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { toast } from 'sonner';
import TopicPicker from '../components/shared/TopicPicker';
import UpgradeModal from '../components/common/UpgradeModal';
import ReportQuestionModal from '../components/common/ReportQuestionModal';
import Pagination from '../components/common/Pagination';
import ModuleAccessOverlay from '../components/common/ModuleAccessOverlay';
import { VideoModal, WatchVideoButton } from '../components/common/VideoModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Nautical grades - surveyors can come from any MMD
const NAUTICAL_GRADES = ['Master', 'Chief Mate', '2MFG'];
const isNauticalGrade = (grade) => NAUTICAL_GRADES.includes(grade);

// Helper function to parse exam_month which can be in different formats:
// - "YYYY-MM" (e.g., "2025-11") - preferred format
// - "Month YYYY" (e.g., "December 2025") - legacy format
// Returns a formatted display string or null if invalid/empty
const formatExamMonth = (examMonth) => {
  if (!examMonth || examMonth.trim() === '') {
    return null;
  }
  
  const trimmed = examMonth.trim();
  
  // Try YYYY-MM format first
  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    const date = new Date(trimmed + '-01');
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    }
  }
  
  // Try "Month YYYY" format (e.g., "December 2025")
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];
  const monthYearMatch = trimmed.match(/^(\w+)\s+(\d{4})$/);
  if (monthYearMatch) {
    const monthName = monthYearMatch[1];
    const year = monthYearMatch[2];
    const monthIndex = months.findIndex(m => m.toLowerCase() === monthName.toLowerCase());
    if (monthIndex !== -1) {
      // Return in same format as the YYYY-MM branch
      const date = new Date(parseInt(year), monthIndex, 1);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    }
  }
  
  // If all else fails, return null (don't show invalid date)
  return null;
};

// Helper function to parse exam_month to Date object for filtering
const parseExamMonthToDate = (examMonth) => {
  if (!examMonth || examMonth.trim() === '') {
    return null;
  }
  
  const trimmed = examMonth.trim();
  
  // Try YYYY-MM format first
  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    const date = new Date(trimmed + '-01');
    if (!isNaN(date.getTime())) {
      return date;
    }
  }
  
  // Try "Month YYYY" format (e.g., "December 2025")
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];
  const monthYearMatch = trimmed.match(/^(\w+)\s+(\d{4})$/);
  if (monthYearMatch) {
    const monthName = monthYearMatch[1];
    const year = monthYearMatch[2];
    const monthIndex = months.findIndex(m => m.toLowerCase() === monthName.toLowerCase());
    if (monthIndex !== -1) {
      return new Date(parseInt(year), monthIndex, 1);
    }
  }
  
  return null;
};

// Helper function to parse markdown-style links [text](url) and render as clickable links
const renderTextWithLinks = (text) => {
  if (!text) return null;
  
  // Regex to match [text](url) pattern
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    
    // Add the link as a React element
    const linkText = match[1];
    const linkUrl = match[2];
    parts.push(
      <a
        key={match.index}
        href={linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-0.5"
        onClick={(e) => e.stopPropagation()}
      >
        {linkText}
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </a>
    );
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after last link
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts.length > 0 ? parts : text;
};

const QuestionBankPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, authLoading } = useAuth();
  const { preferences } = usePreferences();
  
  // Module access check
  const { getOralStatus, loading: moduleLoading } = useModuleAccess();
  const userGrade = preferences?.grade;
  
  // Get oral status for user's selected grade
  // If no grade selected, we'll prompt them to select one
  const oralStatus = userGrade ? getOralStatus(userGrade) : null;
  
  const [questions, setQuestions] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [surveyors, setSurveyors] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [selectedInternalSurveyor, setSelectedInternalSurveyor] = useState('all');
  const [selectedExternalSurveyor, setSelectedExternalSurveyor] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [askedFilter, setAskedFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [completedIds, setCompletedIds] = useState(new Set());
  const [expandedAnswers, setExpandedAnswers] = useState(new Set());
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [surveyorStatus, setSurveyorStatus] = useState('both'); // 'active', 'inactive', 'both' - Default to 'both' so all questions are visible
  const [includePastSurveyors, setIncludePastSurveyors] = useState(false);
  const [inactiveSurveyors, setInactiveSurveyors] = useState([]);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);
  const [showDeciderOnly, setShowDeciderOnly] = useState(false);
  const [showMarqOnly, setShowMarqOnly] = useState(false);
  
  // Combobox open states for searchable dropdowns
  const [topicComboOpen, setTopicComboOpen] = useState(false);
  const [internalSurveyorComboOpen, setInternalSurveyorComboOpen] = useState(false);
  const [externalSurveyorComboOpen, setExternalSurveyorComboOpen] = useState(false);
  
  // Image lightbox state
  const [enlargedImage, setEnlargedImage] = useState(null);
  
  // Report question modal state
  const [reportModal, setReportModal] = useState({ open: false, questionId: null, questionText: '' });
  
  // History modal state for showing asked_by_history
  const [historyModal, setHistoryModal] = useState({ open: false, question: null });

  // Explainer video modal — { open, url }
  const [videoModal, setVideoModal] = useState({ open: false, url: null });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    // Skip if no grade selected - access check will show overlay
    if (!preferences.grade || !preferences.mmd_location) {
      return;
    }
    // Wait for AuthContext to hydrate /auth/me on refresh. Otherwise
    // `isAuthenticated()` returns false (user not yet loaded) and we'd
    // stick the page on the "free" subscription path until full reload.
    if (authLoading) {
      return;
    }

    fetchFunctions();
    fetchTopics();
    fetchSurveyors();
    
    // Only check subscription if authenticated
    if (isAuthenticated()) {
      checkSubscription();
    } else {
      // Guest users default to free status (answers locked)
      setSubscriptionStatus({ subscription_status: 'free', is_active: false });
    }
  }, [authLoading, preferences.grade, preferences.mmd_location]);

  const checkSubscription = async () => {
    try {
      const response = await axios.get(`${API}/user/subscription`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to check subscription:', error);
      // If API fails, default to free
      setSubscriptionStatus({ subscription_status: 'free', is_active: false });
    }
  };

  useEffect(() => {
    if (selectedFunction) {
      fetchQuestions();
    }
  }, [selectedFunction, selectedTopic, selectedInternalSurveyor, selectedExternalSurveyor, dateFrom, dateTo, surveyorStatus]);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, askedFilter, selectedFunction, selectedTopic, selectedInternalSurveyor, selectedExternalSurveyor, dateFrom, dateTo]);

  const fetchFunctions = async () => {
    try {
      const response = await axios.get(`${API}/functions`);
      // Filter by grade AND only show oral functions (exclude written)
      const filteredFunctions = response.data.filter(f => 
        f.grade === preferences.grade && 
        f.exam_type !== 'written'
      );
      setFunctions(filteredFunctions);
    } catch (error) {
      console.error('Failed to fetch functions:', error);
      toast.error('Failed to load functions');
    }
  };

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API}/topics`);
      const filteredTopics = response.data.filter(t => t.grade === preferences.grade);
      setTopics(filteredTopics);
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    }
  };

  const fetchSurveyors = async () => {
    try {
      // Fetch all surveyors (both active and inactive)
      const response = await axios.get(`${API}/surveyors`);
      
      // For Nautical grades: Show ALL surveyors for the grade (any MMD)
      // For Engineering grades: Show surveyors filtered by Grade + MMD
      const filteredSurveyors = response.data.filter(s => {
        const hasGrade = s.grades.includes(preferences.grade);
        
        // Nautical grades: only filter by grade
        if (isNauticalGrade(preferences.grade)) {
          return hasGrade;
        }
        
        // Engineering grades: filter by grade AND MMD
        // Handle both old format ("Kolkata") and new format ("Kolkata MMD")
        const userMMD = preferences.mmd_location;
        const userMMDNormalized = userMMD?.endsWith(' MMD') ? userMMD : `${userMMD} MMD`;
        const hasMMD = s.mmd_locations.includes(userMMD) || s.mmd_locations.includes(userMMDNormalized);
        
        return hasGrade && hasMMD;
      });
      
      // Separate active and inactive
      const active = filteredSurveyors.filter(s => s.is_active !== false);
      const inactive = filteredSurveyors.filter(s => s.is_active === false);
      
      // Store ALL surveyors (for name lookup) but keep inactive list separate (for filtering options)
      setSurveyors(filteredSurveyors);  // Changed: Store all surveyors, not just active
      setInactiveSurveyors(inactive);
    } catch (error) {
      console.error('Failed to fetch surveyors:', error);
    }
  };

  const fetchQuestions = async () => {
    if (!selectedFunction) return;

    try {
      setLoading(true);
      const params = {
        grade: preferences.grade,
        function_id: selectedFunction
      };
      
      // For Engineering grades, also filter by MMD
      // For Nautical grades, show all questions for the grade (surveyors can come from any MMD)
      if (!isNauticalGrade(preferences.grade)) {
        params.mmd_location = preferences.mmd_location;
      }

      if (selectedTopic !== 'all') {
        params.topic_id = selectedTopic;
      }

      if (selectedInternalSurveyor !== 'all') {
        params.surveyor_internal_id = selectedInternalSurveyor;
      }
      
      if (selectedExternalSurveyor !== 'all') {
        params.surveyor_external_id = selectedExternalSurveyor;
      }

      // Handle active/inactive surveyor filtering
      if (surveyorStatus === 'inactive') {
        params.inactive_surveyors_only = true;
      } else if (surveyorStatus === 'both') {
        params.include_inactive = true;
      }
      // Default (active) doesn't need extra params - backend excludes inactive by default

      const response = await axios.get(`${API}/user/questions`, { params });
      
      // Filter by exam month date range if selected
      let filteredQuestions = response.data;
      if (dateFrom || dateTo) {
        const fromDate = dateFrom ? new Date(dateFrom + '-01') : null;
        // Set toDate to the last day of the month for inclusive end range
        const toDate = dateTo ? new Date(parseInt(dateTo.split('-')[0]), parseInt(dateTo.split('-')[1]), 0) : null;
        
        // Helper to check if a date is in range
        const isDateInRange = (date) => {
          if (!date) return false;
          if (fromDate && toDate) {
            return date >= fromDate && date <= toDate;
          } else if (fromDate) {
            return date >= fromDate;
          } else if (toDate) {
            return date <= toDate;
          }
          return true;
        };
        
        filteredQuestions = response.data.filter(q => {
          // Check top-level exam_month
          const topLevelDate = parseExamMonthToDate(q.exam_month);
          if (isDateInRange(topLevelDate)) {
            return true;
          }
          
          // Also check asked_by_history entries
          const history = q.asked_by_history || [];
          for (const entry of history) {
            const historyDate = parseExamMonthToDate(entry.exam_month);
            if (isDateInRange(historyDate)) {
              return true;
            }
          }
          
          // If no valid dates found in either place, exclude the question
          return false;
        });
      }
      
      setQuestions(filteredQuestions);

      // Build set of completed question IDs
      const completed = new Set(
        filteredQuestions.filter(q => q.completed).map(q => q.id)
      );
      setCompletedIds(completed);

      // Build set of bookmarked question IDs
      const bookmarked = new Set(
        filteredQuestions.filter(q => q.bookmarked).map(q => q.id)
      );
      setBookmarkedIds(bookmarked);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleComplete = async (questionId, currentStatus) => {
    try {
      await axios.post(`${API}/user/progress/complete`, null, {
        params: {
          question_id: questionId,
          completed: !currentStatus
        }
      });

      // Update local state
      const newCompletedIds = new Set(completedIds);
      if (currentStatus) {
        newCompletedIds.delete(questionId);
        toast.success('Question marked as incomplete');
      } else {
        newCompletedIds.add(questionId);
        toast.success('Question marked as complete');
      }
      setCompletedIds(newCompletedIds);

      // Update questions array
      setQuestions(questions.map(q => 
        q.id === questionId ? { ...q, completed: !currentStatus } : q
      ));
    } catch (error) {
      console.error('Failed to update completion status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleResetCompleted = async () => {
    if (!selectedFunction) return;
    
    if (!window.confirm('Are you sure you want to reset all completed progress for this function? This will mark all questions as not completed. Viewed and bookmarked status will be preserved.')) {
      return;
    }

    try {
      const response = await axios.post(`${API}/progress/reset-completed/${selectedFunction}`, null, {
        params: {
          grade: preferences.grade,
          mmd_location: preferences.mmd_location
        }
      });
      toast.success(response.data.message || 'Progress reset successfully');
      
      // Reset local completed state for this function's questions
      const functionQuestionIds = questions.map(q => q.id);
      const newCompletedIds = new Set(completedIds);
      functionQuestionIds.forEach(id => newCompletedIds.delete(id));
      setCompletedIds(newCompletedIds);
      
      // Update questions array
      setQuestions(questions.map(q => ({ ...q, completed: false })));
    } catch (error) {
      console.error('Failed to reset progress:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset progress');
    }
  };

  const handleToggleBookmark = async (questionId, currentStatus) => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      toast.error('Please log in to bookmark questions');
      return;
    }

    try {
      const response = await axios.post(`${API}/progress/action`, {
        question_id: questionId,
        action: 'bookmark'
      });

      // Update local state
      const newBookmarkedIds = new Set(bookmarkedIds);
      const isNowBookmarked = response.data.bookmarked;
      
      if (isNowBookmarked) {
        newBookmarkedIds.add(questionId);
        toast.success('Question bookmarked');
      } else {
        newBookmarkedIds.delete(questionId);
        toast.success('Bookmark removed');
      }
      setBookmarkedIds(newBookmarkedIds);

      // Update questions array
      setQuestions(questions.map(q => 
        q.id === questionId ? { ...q, bookmarked: isNowBookmarked } : q
      ));
    } catch (error) {
      console.error('Failed to update bookmark status:', error);
      toast.error('Failed to update bookmark');
    }
  };

  const getTopicsForFunction = () => {
    return topics.filter(t => t.function_id === selectedFunction);
  };

  // Organize topics into parent-child hierarchy for display
  const getHierarchicalTopics = useMemo(() => {
    const availableTopics = getTopicsForFunction();
    
    // Separate parents (no parent_topic_id) and children
    const parentTopics = availableTopics.filter(t => !t.parent_topic_id);
    const childTopics = availableTopics.filter(t => t.parent_topic_id);
    
    // Build hierarchy
    const hierarchy = [];
    parentTopics.forEach(parent => {
      hierarchy.push({ ...parent, isParent: true, level: 0 });
      const children = childTopics.filter(c => c.parent_topic_id === parent.id);
      children.forEach(child => {
        hierarchy.push({ ...child, isParent: false, level: 1 });
      });
    });
    
    // Add orphan children (topics with parent_topic_id but parent not in current filter)
    const addedChildIds = new Set(hierarchy.filter(t => t.level === 1).map(t => t.id));
    childTopics.forEach(child => {
      if (!addedChildIds.has(child.id)) {
        hierarchy.push({ ...child, isParent: false, level: 0 }); // Show as top-level
      }
    });
    
    return hierarchy;
  }, [topics, selectedFunction]);

  const getSurveyorNames = (question) => {
    const result = [];
    
    if (question.surveyor_internal_id) {
      const surveyor = surveyors.find(s => s.id === question.surveyor_internal_id);
      const displayName = surveyor 
        ? `${surveyor.name}${surveyor.is_active === false ? ' (Inactive)' : ''}` 
        : 'Unknown';
      result.push({ name: displayName, type: 'Internal' });
    } else if (question.surveyor_internal_other) {
      result.push({ name: question.surveyor_internal_other, type: 'Internal' });
    }
    
    if (question.surveyor_external_id) {
      const surveyor = surveyors.find(s => s.id === question.surveyor_external_id);
      const displayName = surveyor 
        ? `${surveyor.name}${surveyor.is_active === false ? ' (Inactive)' : ''}` 
        : 'Unknown';
      result.push({ name: displayName, type: 'External' });
    } else if (question.surveyor_external_other) {
      result.push({ name: question.surveyor_external_other, type: 'External' });
    }
    
    return result.length > 0 ? result : [{ name: 'Not specified', type: '' }];
  };

  // Check if question has additional surveyor history (asked_by_history)
  const hasAskedByHistory = (question) => {
    return question.asked_by_history && question.asked_by_history.length > 0;
  };

  // Get full history for display in modal - just use asked_by_history as-is
  // (Backend already includes original surveyor when merging)
  // Group by surveyor + exam_month for cleaner display
  const getFullHistory = (question) => {
    const history = question.asked_by_history || [];
    
    // If no history, show the original surveyor from top-level fields
    if (history.length === 0) {
      const originalSurveyorId = question.surveyor_internal_id || question.surveyor_external_id;
      const surveyorType = question.surveyor_internal_id ? 'Internal' : 'External';
      if (originalSurveyorId) {
        const surveyor = surveyors.find(s => s.id === originalSurveyorId);
        return [{
          surveyor_name: surveyor?.name || 'Unknown Surveyor',
          surveyor_type: surveyorType,
          exam_month: question.exam_month,
          mmd_location: question.mmd_location,
          count: 1
        }];
      }
      return [];
    }
    
    // Normalize the surveyor identifier so legacy "unknown" sentinels and
    // the proper "unknown-{type}-active" IDs are treated as the SAME group.
    const SENTINEL_IDS = new Set(['unknown', 'unknown-internal-active', 'unknown-external-active']);
    const normSurveyorKey = (entry) => {
      const sid = entry.surveyor_id;
      if (!sid || SENTINEL_IDS.has(sid)) return `_unknown_${entry.surveyor_type || 'external'}`;
      return sid;
    };
    const normMonthKey = (em) => {
      if (!em || !String(em).trim()) return '_undated';
      return String(em).trim().toLowerCase().replace(/\s+/g, ' ');
    };

    // Group entries by normalized (surveyor, exam_month)
    const grouped = {};
    history.forEach(entry => {
      const surveyor = surveyors.find(s => s.id === entry.surveyor_id);
      const key = `${normSurveyorKey(entry)}|${normMonthKey(entry.exam_month)}`;

      // Friendly fallback for legacy rows where surveyor_id was saved as the
      // literal string "unknown": show "Unknown Internal"/"Unknown External"
      // based on surveyor_type instead of the raw id.
      const typeLabel = entry.surveyor_type === 'internal' ? 'Internal' : 'External';
      const fallbackName = `Unknown ${typeLabel}`;
      const displayName = surveyor?.name
        || entry.surveyor_name
        || ((!entry.surveyor_id || entry.surveyor_id === 'unknown') ? fallbackName : (entry.surveyor_id || fallbackName));

      if (!grouped[key]) {
        // For undated entries, derive a sensible display date.
        let displayMonth = entry.exam_month;
        if (!displayMonth || !String(displayMonth).trim()) {
          const ts = entry.added_at ? new Date(entry.added_at) : new Date();
          if (!isNaN(ts.getTime())) {
            displayMonth = ts.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
          }
        }
        grouped[key] = {
          surveyor_name: displayName,
          surveyor_type: typeLabel,
          exam_month: displayMonth,
          mmd_location: entry.mmd_location,
          count: 0
        };
      }
      grouped[key].count += entry.asked || 1;
    });
    
    // Convert to array and sort by exam_month (newest first)
    return Object.values(grouped).sort((a, b) => {
      if (!a.exam_month) return 1;
      if (!b.exam_month) return -1;
      return b.exam_month.localeCompare(a.exam_month);
    });
  };

  const clearDateRange = () => {
    setDateFrom('');
    setDateTo('');
  };

  const toggleAnswer = (questionId) => {
    // Check if user has paid access for the current grade
    const subscribedGrade = subscriptionStatus?.subscribed_grade;
    const userRole = subscriptionStatus?.user_role;
    const currentGrade = preferences.grade;
    
    // Admins/sub-admins have access to all grades
    const isAdmin = userRole === 'admin' || userRole === 'sub_admin';
    
    // Check if user has access: either admin, or subscribed to 'all', or subscribed to current grade
    const hasGradeAccess = isAdmin || subscribedGrade === 'all' || subscribedGrade === currentGrade;
    const hasAccess = subscriptionStatus?.subscription_status === 'paid' && subscriptionStatus?.is_active && hasGradeAccess;
    
    if (!hasAccess) {
      // User doesn't have access - show upgrade modal
      setShowUpgrade(true);
      return;
    }
    
    // User has access - toggle answer
    const newExpanded = new Set(expandedAnswers);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedAnswers(newExpanded);
  };

  const getTopicName = (topicId) => {
    const topic = topics.find(t => t.id === topicId);
    return topic ? topic.name : null;
  };

  // Filter questions by search query, asked filter, and bookmarked filter
  const allFilteredQuestions = questions.filter(q => {
    // Search filter
    const matchesSearch = q.question_text.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Asked filter
    let matchesAsked = true;
    if (askedFilter === 'once') {
      matchesAsked = q.asked === 1;
    } else if (askedFilter === 'multiple') {
      matchesAsked = q.asked > 1;
    }
    // 'all' doesn't filter
    
    // Bookmarked filter
    const matchesBookmarked = !showBookmarkedOnly || bookmarkedIds.has(q.id);
    
    // Decider filter
    const matchesDecider = !showDeciderOnly || q.is_decider;
    
    // MARQ Recommended filter
    const matchesMarq = !showMarqOnly || q.is_marq_recommended;
    
    return matchesSearch && matchesAsked && matchesBookmarked && matchesDecider && matchesMarq;
  });

  // Pagination logic
  const totalCount = allFilteredQuestions.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const filteredQuestions = allFilteredQuestions.slice(startIndex, endIndex);
  
  const completedCount = allFilteredQuestions.filter(q => completedIds.has(q.id)).length;
  const completionPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Module access enforcement - show overlay if oral is not active
  // Also handle case where user hasn't selected a grade yet
  if (!moduleLoading) {
    // No grade or MMD selected - prompt user to select
    if (!userGrade || !preferences?.mmd_location) {
      return (
        <Layout>
          <ModuleAccessOverlay
            status="disabled"
            featureName="Oral Question Bank"
            customMessage="Please select your grade and MMD location first to access the Oral Question Bank."
            showBackButton={true}
            showHomeButton={false}
          />
          <div className="flex justify-center mt-4">
            <Button
              onClick={() => navigate('/grade')}
              className="bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
            >
              Select Grade & MMD
            </Button>
          </div>
        </Layout>
      );
    }
    
    // Grade selected but module is not active for this grade
    if (oralStatus && oralStatus !== 'active') {
      return (
        <Layout>
          <ModuleAccessOverlay
            status={oralStatus}
            featureName="Oral Question Bank"
            description={
              oralStatus === 'coming_soon'
                ? `The Oral Question Bank for ${userGrade} is coming soon! We're preparing this content and it will be available shortly.`
                : oralStatus === 'hidden'
                  ? `The Oral Question Bank is not available for ${userGrade}. Please select a different grade or contact support.`
                  : `The Oral Question Bank is currently not available for ${userGrade}.`
            }
          />
        </Layout>
      );
    }
  }

  return (
    <Layout>
      <div className="page-transition max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#0A7EA4]/10 rounded-lg flex-shrink-0">
              <BookOpen className="w-6 h-6 text-[#0A7EA4]" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Oral Question Bank</h1>
          </div>
          <p className="text-sm sm:text-base text-gray-600">
            Browse and study oral exam questions for {preferences.grade} - {preferences.mmd_location}
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Filter className="w-5 h-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Function Filter (Mandatory) - First, as search only works after function is selected */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Function <span className="text-red-500">*</span>
                </label>
                <Select value={selectedFunction} onValueChange={setSelectedFunction}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select function" />
                  </SelectTrigger>
                  <SelectContent>
                    {functions.map((func) => {
                      const suffix = func.function_suffix && func.function_suffix !== 'None' && func.function_suffix !== 'null' ? func.function_suffix : '';
                      return (
                        <SelectItem key={func.id} value={func.id}>
                          F{func.function_number}{suffix} - {func.name}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Asked Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Asked Filter
                </label>
                <Select value={askedFilter} onValueChange={setAskedFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Questions</SelectItem>
                    <SelectItem value="once">Asked Once</SelectItem>
                    <SelectItem value="multiple">Asked Multiple Times</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Surveyor Status Filter - NEW */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Surveyor Status
                </label>
                <Select value={surveyorStatus} onValueChange={setSurveyorStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active Surveyors Only</SelectItem>
                    <SelectItem value="inactive">Past Surveyors Only</SelectItem>
                    <SelectItem value="both">Both Active & Past</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Topic Filter (Optional) - Searchable with Hierarchy */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Topic (Optional)
                </label>
                <TopicPicker
                  topics={getTopicsForFunction()}
                  value={selectedTopic === 'all' ? null : selectedTopic}
                  onChange={(id) => setSelectedTopic(id || 'all')}
                  placeholder={!selectedFunction ? 'Select function first…' : 'All Topics'}
                  allowClear
                  disabled={!selectedFunction}
                  testIdPrefix="user-oral-topic-filter"
                />
                {selectedTopic !== 'all' && topics.find(t => t.id === selectedTopic) && (
                  <p className="text-xs text-amber-600 mt-1">
                    ↳ Showing questions from this topic and all sub-topics
                  </p>
                )}
              </div>

              {/* Internal Surveyor Filter (Optional) - Searchable */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Internal Surveyor (Optional)
                </label>
                <Popover open={internalSurveyorComboOpen} onOpenChange={setInternalSurveyorComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={internalSurveyorComboOpen}
                      className="w-full justify-between"
                      disabled={!selectedFunction}
                    >
                      {selectedInternalSurveyor === 'all'
                        ? 'All Internal'
                        : (() => {
                            const allInternal = [...surveyors.filter(s => s.type === 'internal'), ...inactiveSurveyors.filter(s => s.type === 'internal')];
                            const found = allInternal.find(s => s.id === selectedInternalSurveyor);
                            return found ? found.name : 'Select surveyor';
                          })()}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[280px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search internal surveyors..." />
                      <CommandList>
                        <CommandEmpty>No surveyors found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all"
                            onSelect={() => {
                              setSelectedInternalSurveyor('all');
                              setInternalSurveyorComboOpen(false);
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", selectedInternalSurveyor === 'all' ? "opacity-100" : "opacity-0")} />
                            All Internal
                          </CommandItem>
                        </CommandGroup>
                        {/* Active Internal Surveyors */}
                        {surveyorStatus !== 'inactive' && surveyors.filter(s => s.type === 'internal' && s.is_active !== false).length > 0 && (
                          <CommandGroup heading="Active">
                            {surveyors.filter(s => s.type === 'internal' && s.is_active !== false).map((surveyor) => (
                              <CommandItem
                                key={surveyor.id}
                                value={surveyor.name}
                                onSelect={() => {
                                  setSelectedInternalSurveyor(surveyor.id);
                                  setInternalSurveyorComboOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", selectedInternalSurveyor === surveyor.id ? "opacity-100" : "opacity-0")} />
                                {surveyor.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                        {/* Inactive Internal Surveyors */}
                        {surveyorStatus !== 'active' && inactiveSurveyors.filter(s => s.type === 'internal').length > 0 && (
                          <CommandGroup heading="Past Surveyors">
                            {inactiveSurveyors.filter(s => s.type === 'internal').map((surveyor) => (
                              <CommandItem
                                key={surveyor.id}
                                value={`${surveyor.name} inactive`}
                                onSelect={() => {
                                  setSelectedInternalSurveyor(surveyor.id);
                                  setInternalSurveyorComboOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", selectedInternalSurveyor === surveyor.id ? "opacity-100" : "opacity-0")} />
                                {surveyor.name} {surveyor.inactive_reason === 'transferred' && surveyor.transferred_to_mmd ? `(→ ${surveyor.transferred_to_mmd})` : '(Inactive)'}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* External Surveyor Filter (Optional) - Searchable */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  External Surveyor (Optional)
                </label>
                <Popover open={externalSurveyorComboOpen} onOpenChange={setExternalSurveyorComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={externalSurveyorComboOpen}
                      className="w-full justify-between"
                      disabled={!selectedFunction}
                    >
                      {selectedExternalSurveyor === 'all'
                        ? 'All External'
                        : (() => {
                            const allExternal = [...surveyors.filter(s => s.type === 'external'), ...inactiveSurveyors.filter(s => s.type === 'external')];
                            const found = allExternal.find(s => s.id === selectedExternalSurveyor);
                            return found ? found.name : 'Select surveyor';
                          })()}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[280px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search external surveyors..." />
                      <CommandList>
                        <CommandEmpty>No surveyors found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all"
                            onSelect={() => {
                              setSelectedExternalSurveyor('all');
                              setExternalSurveyorComboOpen(false);
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", selectedExternalSurveyor === 'all' ? "opacity-100" : "opacity-0")} />
                            All External
                          </CommandItem>
                        </CommandGroup>
                        {/* Active External Surveyors */}
                        {surveyorStatus !== 'inactive' && surveyors.filter(s => s.type === 'external' && s.is_active !== false).length > 0 && (
                          <CommandGroup heading="Active">
                            {surveyors.filter(s => s.type === 'external' && s.is_active !== false).map((surveyor) => (
                              <CommandItem
                                key={surveyor.id}
                                value={surveyor.name}
                                onSelect={() => {
                                  setSelectedExternalSurveyor(surveyor.id);
                                  setExternalSurveyorComboOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", selectedExternalSurveyor === surveyor.id ? "opacity-100" : "opacity-0")} />
                                {surveyor.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                        {/* Inactive External Surveyors */}
                        {surveyorStatus !== 'active' && inactiveSurveyors.filter(s => s.type === 'external').length > 0 && (
                          <CommandGroup heading="Past Surveyors">
                            {inactiveSurveyors.filter(s => s.type === 'external').map((surveyor) => (
                              <CommandItem
                                key={surveyor.id}
                                value={`${surveyor.name} inactive`}
                                onSelect={() => {
                                  setSelectedExternalSurveyor(surveyor.id);
                                  setExternalSurveyorComboOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", selectedExternalSurveyor === surveyor.id ? "opacity-100" : "opacity-0")} />
                                {surveyor.name} {surveyor.inactive_reason === 'retired' ? '(Retired)' : '(Inactive)'}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

            </div>

            {/* Search Bar - Only shown after function is selected */}
            {selectedFunction && (
              <div className="mt-4 pt-4 border-t">
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Search Questions
                </label>
                <Input
                  type="text"
                  placeholder="Search by question text..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full"
                />
              </div>
            )}

            {/* Bookmarked Filter Checkbox */}
            {selectedFunction && isAuthenticated() && (
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="show-bookmarked"
                    checked={showBookmarkedOnly}
                    onCheckedChange={setShowBookmarkedOnly}
                  />
                  <label
                    htmlFor="show-bookmarked"
                    className="text-sm font-medium text-gray-700 cursor-pointer select-none flex items-center gap-1"
                  >
                    <Bookmark className="w-4 h-4" />
                    Bookmarked Only
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="show-decider"
                    checked={showDeciderOnly}
                    onCheckedChange={setShowDeciderOnly}
                  />
                  <label
                    htmlFor="show-decider"
                    className="text-sm font-medium text-red-700 cursor-pointer select-none"
                  >
                    Decider Questions Only
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="show-marq"
                    checked={showMarqOnly}
                    onCheckedChange={setShowMarqOnly}
                  />
                  <label
                    htmlFor="show-marq"
                    className="text-sm font-medium text-purple-700 cursor-pointer select-none"
                  >
                    MARQ Recommended
                  </label>
                </div>
              </div>
            )}

            {/* Date Range Filter Row */}
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">
                  Exam Date Range (Optional)
                </label>
                {(dateFrom || dateTo) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearDateRange}
                    className="text-xs"
                  >
                    Clear Range
                  </Button>
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">From Month/Year</label>
                  <MonthPicker
                    value={dateFrom}
                    onChange={setDateFrom}
                    disabled={!selectedFunction}
                    placeholder="Select start month"
                    fromYear={2015}
                    toYear={new Date().getFullYear()}
                    maxMonth={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">To Month/Year (or Present)</label>
                  <MonthPicker
                    value={dateTo}
                    onChange={setDateTo}
                    disabled={!selectedFunction}
                    minMonth={dateFrom}
                    placeholder="Select end month"
                    fromYear={2015}
                    toYear={new Date().getFullYear()}
                    maxMonth={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
                  />
                </div>
              </div>
              
              <p className="text-xs text-gray-500 mt-2">
                Filter questions by exam date range. Leave &quot;To&quot; empty to include all questions from selected start date to present.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Progress Summary */}
        {selectedFunction && questions.length > 0 && (
          <Card className="mb-6 bg-gradient-to-r from-[#0A7EA4]/5 to-[#14B8A6]/5">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Your Progress</div>
                  <div className="text-2xl font-bold text-[#0A7EA4]">
                    {completedCount} / {totalCount} Questions
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-3xl font-bold text-[#10B981]">{completionPercentage}%</div>
                    <div className="text-sm text-gray-600">Complete</div>
                  </div>
                  {completedCount > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetCompleted}
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Questions List */}
        <div className="space-y-4">
          {!selectedFunction ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Filter className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Select a Function</h3>
                <p className="text-gray-500">Choose a function from the filters above to view questions</p>
              </CardContent>
            </Card>
          ) : loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No questions found</h3>
                <p className="text-gray-500">
                  No approved questions match your selected filters or search query
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredQuestions.map((question, index) => {
              const isCompleted = completedIds.has(question.id);
              
              const surveyorInfo = getSurveyorNames(question);
              const isAnswerExpanded = expandedAnswers.has(question.id);
              
              return (
                <Card
                  key={question.id}
                  className={`transition-all ${
                    isCompleted 
                      ? 'bg-green-50/50 border-green-200' 
                      : 'hover:shadow-md'
                  }`}
                >
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      {/* Completion Checkbox */}
                      <div className="flex items-start pt-1">
                        <button
                          onClick={() => handleToggleComplete(question.id, isCompleted)}
                          className="focus:outline-none focus:ring-2 focus:ring-[#0A7EA4] rounded"
                        >
                          {isCompleted ? (
                            <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
                          ) : (
                            <Circle className="w-6 h-6 text-gray-300 hover:text-[#0A7EA4]" />
                          )}
                        </button>
                      </div>

                      {/* Question Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-3">
                          <h3 className={`text-base sm:text-lg font-semibold ${
                            isCompleted ? 'text-gray-600 line-through' : 'text-gray-800'
                          }`}>
                            {question.question_text}
                          </h3>
                          <div className="flex gap-2 flex-shrink-0">
                            {isAuthenticated() && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleToggleBookmark(question.id, bookmarkedIds.has(question.id))}
                                  className={`flex items-center gap-1 ${
                                    bookmarkedIds.has(question.id) ? 'bg-yellow-50 border-yellow-300' : ''
                                  }`}
                                  title="Bookmark"
                                >
                                  <Bookmark 
                                    className={`w-4 h-4 ${
                                      bookmarkedIds.has(question.id) ? 'fill-yellow-500 text-yellow-500' : ''
                                    }`} 
                                  />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setReportModal({ 
                                    open: true, 
                                    questionId: question.id, 
                                    questionText: question.question_text 
                                  })}
                                  className="flex items-center gap-1 hover:bg-red-50 hover:border-red-300 hover:text-red-600"
                                  title="Report Issue"
                                >
                                  <Flag className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleAnswer(question.id)}
                              className="flex items-center gap-1"
                            >
                              {isAnswerExpanded ? (
                                <>
                                  <ChevronUp className="w-4 h-4" />
                                  <span className="hidden sm:inline">Hide Answer</span>
                                  <span className="sm:hidden">Hide</span>
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="w-4 h-4" />
                                  <span className="hidden sm:inline">View Answer</span>
                                  <span className="sm:hidden">View</span>
                                </>
                              )}
                            </Button>
                          </div>
                        </div>

                        {/* Badges */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          <Badge className="bg-[#0A7EA4]">
                            {preferences.grade}
                          </Badge>
                          <Badge className="bg-[#14B8A6]">
                            {preferences.mmd_location}
                          </Badge>
                          {formatExamMonth(question.exam_month) && (
                            <Badge variant="outline" className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatExamMonth(question.exam_month)}
                            </Badge>
                          )}
                          {/* Display multiple topics */}
                          {(question.topic_ids?.length > 0 || question.topic_id) ? (
                            (question.topic_ids?.length > 0 ? question.topic_ids : [question.topic_id])
                              .map(tid => getTopicName(tid))
                              .filter(Boolean)
                              .map((topicName, idx) => (
                                <Badge key={idx} className="bg-purple-600 flex items-start gap-1 max-w-full whitespace-normal break-words text-left leading-snug">
                                  <Tag className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                  <span className="min-w-0 break-words">{topicName}</span>
                                </Badge>
                              ))
                          ) : null}
                          {question.is_decider && (
                            <Badge className="bg-red-500">Decider</Badge>
                          )}
                          {question.is_marq_recommended && (
                            <Badge className="bg-indigo-600">MARQ Recommended</Badge>
                          )}
                          {question.asked > 1 && (
                            <Badge className="bg-orange-600">
                              Asked {question.asked}x
                            </Badge>
                          )}
                          {surveyorInfo.map((surveyor, idx) => (
                            <Badge key={idx} variant="outline" className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {surveyor.name} {surveyor.type && `(${surveyor.type})`}
                            </Badge>
                          ))}
                          {/* History icon - shows when there are multiple surveyors who asked this question */}
                          {hasAskedByHistory(question) && (
                            <button
                              onClick={() => setHistoryModal({ open: true, question })}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border border-blue-300 bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors cursor-pointer"
                              title="View all surveyors who asked this question"
                            >
                              <History className="w-3 h-3" />
                              +{question.asked_by_history.length} more
                            </button>
                          )}
                          {isCompleted && (
                            <Badge className="bg-green-600">
                              ✓ Completed
                            </Badge>
                          )}
                          {bookmarkedIds.has(question.id) && (
                            <Badge className="bg-yellow-500 flex items-center gap-1">
                              <Bookmark className="w-3 h-3" />
                              Bookmarked
                            </Badge>
                          )}
                        </div>

                        {/* Expandable Answer Section */}
                        {isAnswerExpanded && (
                          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            {question.has_answer ? (
                              <div className="space-y-3">
                                {question.answer_text && (
                                  <div>
                                    <h4 className="text-sm font-semibold text-blue-900 mb-1 flex items-center gap-2">
                                      Answer:
                                      {question.video_url && (
                                        <WatchVideoButton
                                          videoUrl={question.video_url}
                                          onClick={() => setVideoModal({ open: true, url: question.video_url })}
                                        />
                                      )}
                                    </h4>
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{renderTextWithLinks(question.answer_text)}</p>
                                  </div>
                                )}
                                {/* Answer Images */}
                                {question.answer_images && question.answer_images.length > 0 && (
                                  <div>
                                    <h4 className="text-sm font-semibold text-blue-900 mb-2">Diagrams/Images:</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                      {question.answer_images.map((img, idx) => (
                                        <div 
                                          key={img.id || idx} 
                                          className="bg-white rounded-lg border overflow-hidden cursor-pointer group relative"
                                          onClick={() => setEnlargedImage({ url: img.url, caption: img.caption })}
                                        >
                                          <div className="relative">
                                            <img 
                                              src={`${BACKEND_URL}${img.url}`} 
                                              alt={img.caption || `Diagram ${idx + 1}`}
                                              className="w-full h-auto max-h-64 object-contain transition-opacity group-hover:opacity-90"
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                                              <ZoomIn className="w-8 h-8 text-white drop-shadow-lg" />
                                            </div>
                                          </div>
                                          {img.caption && (
                                            <p className="text-xs text-gray-600 p-2 bg-gray-50 border-t">
                                              {img.caption}
                                            </p>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {question.explanation && (
                                  <div>
                                    <h4 className="text-sm font-semibold text-blue-900 mb-1">Explanation:</h4>
                                    <p className="text-sm text-gray-700">{question.explanation}</p>
                                  </div>
                                )}
                                {question.references && (
                                  <div>
                                    <h4 className="text-sm font-semibold text-blue-900 mb-1">References:</h4>
                                    <p className="text-sm text-gray-700">{question.references}</p>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <div className="text-gray-500 mb-2">
                                  <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                  <p className="text-sm">Coming Soon</p>
                                  <p className="text-xs mt-1">Answer is under review</p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
          
          {/* Pagination */}
          {allFilteredQuestions.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
              onItemsPerPageChange={(perPage) => {
                setItemsPerPage(perPage);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
      </div>
      
      <UpgradeModal 
        isOpen={showUpgrade} 
        onClose={() => setShowUpgrade(false)} 
        feature="answers"
        subscribedGrade={subscriptionStatus?.subscribed_grade}
        targetGrade={preferences.grade}
      />

      {/* Image Lightbox Modal */}
      <Dialog open={!!enlargedImage} onOpenChange={() => setEnlargedImage(null)}>
        <DialogContent className="max-w-4xl w-full p-0 bg-black/95 border-none">
          <button
            onClick={() => setEnlargedImage(null)}
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          {enlargedImage && (
            <div className="flex flex-col items-center justify-center p-4">
              <img
                src={`${BACKEND_URL}${enlargedImage.url}`}
                alt={enlargedImage.caption || 'Enlarged image'}
                className="max-w-full max-h-[80vh] object-contain"
              />
              {enlargedImage.caption && (
                <p className="text-white text-center mt-4 text-sm">{enlargedImage.caption}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Question Modal */}
      <ReportQuestionModal
        open={reportModal.open}
        onClose={() => setReportModal({ open: false, questionId: null, questionText: '' })}
        questionId={reportModal.questionId}
        questionText={reportModal.questionText}
      />

      {/* Explainer Video Modal — YouTube / Dadan iframe */}
      {videoModal.open && (
        <VideoModal
          videoUrl={videoModal.url}
          onClose={() => setVideoModal({ open: false, url: null })}
        />
      )}

      {/* History Modal - Shows all surveyors who asked the question */}
      <Dialog open={historyModal.open} onOpenChange={(open) => !open && setHistoryModal({ open: false, question: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Question History
            </DialogTitle>
            <DialogDescription>
              All surveyors who asked this question
            </DialogDescription>
          </DialogHeader>
          {historyModal.question && (
            <div className="mt-4 max-h-[60vh] overflow-y-auto pr-2 space-y-3">
              {getFullHistory(historyModal.question).map((entry, idx) => (
                <div 
                  key={idx} 
                  className="p-3 rounded-lg border bg-gray-50 border-gray-200"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900 flex items-center gap-1.5">
                      <User className="w-4 h-4 text-gray-500" />
                      {entry.surveyor_name}
                      {entry.count > 1 && (
                        <Badge className="bg-orange-500 text-white text-xs ml-1">
                          x{entry.count}
                        </Badge>
                      )}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {entry.surveyor_type}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    {entry.exam_month && formatExamMonth(entry.exam_month) && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatExamMonth(entry.exam_month)}
                      </span>
                    )}
                    {entry.mmd_location && (
                      <span className="text-gray-500">
                        {entry.mmd_location}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default QuestionBankPage;
